import MetaLeadIntegrationsAPI from '../../api/metaLeadIntegrations';
import MetaLeadFormsAPI from '../../api/metaLeadForms';
import InboxesAPI from '../../api/inboxes';

// O backend pagina o historico de leads em 25
// (`LeadIntegrationsController::EVENTS_PER_PAGE`). O numero vive aqui tambem
// porque o `TableFooter` calcula o total de paginas a partir dele, e a
// resposta so traz o tamanho da pagina quando a chamada ja aconteceu.
export const EVENTS_PAGE_SIZE = 25;

const state = {
  integrations: [],
  inboxes: [],
  flows: [],
  events: [],
  eventsMeta: {
    count: 0,
    currentPage: 1,
    pageSize: EVENTS_PAGE_SIZE,
  },
  uiFlags: {
    isFetching: false,
    isFetchingEvents: false,
  },
};

export const getters = {
  getIntegrations(_state) {
    return _state.integrations;
  },
  getInboxes(_state) {
    return _state.inboxes;
  },
  getFlows(_state) {
    return _state.flows;
  },
  getEvents(_state) {
    return _state.events;
  },
  getEventsMeta(_state) {
    return _state.eventsMeta;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  async get({ commit }) {
    commit('setFetching', true);
    try {
      const response = await MetaLeadIntegrationsAPI.get();
      commit('setIntegrations', response.data.payload.integrations || []);
    } finally {
      commit('setFetching', false);
    }
  },

  // Fluxos manuais publicados da conta — a acao pos-lead deixou de ser macro na
  // #758. A lista vem do store de Automacoes, nao mais de `macrosAPI`.
  //
  // `fetchAllForSelect` ja faz o laco de paginacao dentro do client (issue
  // #663): pedir a lista sem pagina devolveria so os 15 primeiros por ordem
  // alfabetica e o fluxo procurado simplesmente nao estaria la, sem nada dizer
  // que a lista foi cortada.
  //
  // Falha degrada em silencio para lista vazia — um 403 (papel sem permissao de
  // automacao) ou queda de rede nao pode derrubar a tela inteira do Meta Lead
  // Ads, que faz muito mais coisa do que escolher a acao pos-lead.
  async fetchManualFlows({ commit, dispatch }) {
    try {
      const records = await dispatch(
        'automationFlows/fetchAllForSelect',
        {
          triggerType: 'manual',
          // `status` e o filtro da TELA de fluxos: olha so
          // `published_version_id`. `published: true` cairia no
          // `only_runnable`, que tambem exige `active: true` — fluxo apenas
          // PAUSADO sumiria da lista e a tela o chamaria de "Fluxo
          // indisponivel", sendo que para o backend a configuracao segue
          // integra (`usable_scope` deixa `active` de fora de proposito e o
          // disparo so fica adiado ate religarem o fluxo).
          status: 'published',
        },
        { root: true }
      );

      commit(
        'setFlows',
        (records || []).filter(
          flow => flow.trigger_type === 'manual' && flow.published_version_id
        )
      );
    } catch (error) {
      commit('setFlows', []);
    }
  },

  // Canais, fluxos e atributos personalizados sao os mesmos para toda a conta:
  // buscar uma vez e reusar. Antes a tela guardava isso em `ref()` local, entao
  // sair da tela e voltar refazia as chamadas mesmo sem nada ter mudado.
  //
  // `attributes/get` entrou na #1353: o seletor de destino do mapeamento le
  // `attributes/getAttributesByModel`, e a tela nunca despachava a busca — o
  // store respondia lista vazia e o campo era texto livre, onde um typo grava
  // em `custom_attributes` um valor que o painel lateral nunca renderiza. O
  // despacho mora aqui, e nao no `FieldMappingRow`, porque a linha e renderizada
  // uma vez por pergunta do formulario. A acao engole o proprio erro e cai para
  // lista vazia, entao ela nao derruba o resto da tela.
  async fetchReferenceData(
    { commit, dispatch, state: _state },
    { force } = {}
  ) {
    if (!force && _state.inboxes.length) {
      return;
    }

    const [inboxesResponse] = await Promise.all([
      InboxesAPI.get(),
      dispatch('fetchManualFlows'),
      dispatch('attributes/get', null, { root: true }),
    ]);
    commit('setInboxes', inboxesResponse.data.payload || []);
  },

  async getPages(_store, omniauthToken) {
    const response = await MetaLeadIntegrationsAPI.getPages(omniauthToken);
    return response.data.payload;
  },

  async create({ dispatch }, params) {
    await MetaLeadIntegrationsAPI.create({ lead_integration: params });
    await dispatch('get');
  },

  async syncForms({ dispatch }, integrationId) {
    await MetaLeadIntegrationsAPI.syncForms(integrationId);
    await dispatch('get');
  },

  async delete({ dispatch }, integrationId) {
    await MetaLeadIntegrationsAPI.delete(integrationId);
    await dispatch('get');
  },

  async updateForm({ dispatch }, { id, ...params }) {
    await MetaLeadFormsAPI.update(id, { lead_form: params });
    await dispatch('get');
  },

  async fetchEvents({ commit }, { integrationId, page = 1 }) {
    commit('setFetchingEvents', true);
    try {
      const response = await MetaLeadIntegrationsAPI.getEvents(
        integrationId,
        page
      );
      const events = response.data.payload.events || [];
      const meta = response.data.meta || {};

      commit('setEvents', events);
      commit('setEventsMeta', {
        count: meta.count ?? events.length,
        currentPage: Number(meta.current_page) || page,
        pageSize: Number(meta.page_size) || EVENTS_PAGE_SIZE,
      });

      return events;
    } finally {
      commit('setFetchingEvents', false);
    }
  },

  async reprocessEvent({ commit, dispatch }, { integrationId, eventId }) {
    const response = await MetaLeadIntegrationsAPI.reprocessEvent(
      integrationId,
      eventId
    );
    const event = response.data.payload.event;
    commit('updateEvent', event);
    await dispatch('get');
    return event;
  },

  async replayEventAction(
    { commit, dispatch },
    { integrationId, eventId, stepKey }
  ) {
    const response = await MetaLeadIntegrationsAPI.replayEventAction(
      integrationId,
      eventId,
      stepKey
    );
    const event = response.data.payload.event;
    commit('updateEvent', event);
    await dispatch('get');
    return event;
  },

  clearEvents({ commit }) {
    commit('setEvents', []);
    commit('setEventsMeta', {
      count: 0,
      currentPage: 1,
      pageSize: EVENTS_PAGE_SIZE,
    });
  },
};

export const mutations = {
  setIntegrations(_state, data) {
    _state.integrations = data;
  },
  setInboxes(_state, data) {
    _state.inboxes = data;
  },
  setFlows(_state, data) {
    _state.flows = data;
  },
  setEvents(_state, data) {
    _state.events = data;
  },
  setEventsMeta(_state, data) {
    _state.eventsMeta = data;
  },
  updateEvent(_state, data) {
    _state.events = _state.events.map(item =>
      item.id === data.id ? data : item
    );
  },
  setFetching(_state, value) {
    _state.uiFlags.isFetching = value;
  },
  setFetchingEvents(_state, value) {
    _state.uiFlags.isFetchingEvents = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
